import React, { useEffect, useState, useCallback, lazy, Suspense, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { collection, doc, getDoc, updateDoc, Timestamp, addDoc, serverTimestamp, deleteDoc, setDoc, getDocs, increment, query, where, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Event, Location, TicketTier, MediaItem, EventStatus, BookingStatus, PaymentStatus } from '../types/models';
import { useAuth } from '../contexts/AuthContext';
import { format, isPast, isFuture, isToday, parseISO } from 'date-fns';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from '../utils/toast';
import { FirebaseError } from 'firebase/app';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { v4 as uuidv4 } from 'uuid';
import { FiCalendar, FiMapPin, FiUsers, FiDollarSign, FiTag, FiClock, FiInfo, FiPlus, FiMinus, FiCheck, FiLayers, FiShare2, FiHeart, FiCreditCard, FiChevronLeft, FiChevronRight, FiLink, FiTwitter, FiFacebook, FiLinkedin, FiArrowLeft, FiHelpCircle, FiStar, FiTrendingUp, FiEdit2, FiImage, FiChevronUp, FiChevronDown, FiUser, FiAlertCircle } from 'react-icons/fi';
import { Ticket, StarIcon, Sparkles, Loader2 } from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { cn } from '../lib/utils';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import ReviewDialog from '../components/ReviewDialog';
import { TicketBookingCard } from '../components/TicketBookingCard';
import { SimpleMap } from '../components/Map/SimpleMap';
import { MapPin } from 'lucide-react';
import Slider from 'react-slick';
import { getEventById } from '../services/eventService';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ErrorBoundary } from 'react-error-boundary';
import { EventMediaSlider } from '../components/EventMediaSlider';
import { formatDate, formatTime, formatDateTime } from '../utils/dateUtils';
import { BookingSuccessDialog } from '../components/BookingSuccessDialog';
import { getTicketTiersArray, hasAvailableTickets, getMinTicketPrice, isEventPast } from '../utils/eventHelpers';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import axios from 'axios';
import { bookingService } from '../services/bookingService';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { EventCard } from '../components/EventCard';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { DescriptionRenderer } from '../components/DescriptionRenderer';

// Lazy load the Slider component
const SliderComponent = lazy(() => import('react-slick'));

// Update the placeholder image to use a simpler SVG
const PLACEHOLDER_IMAGE = '/images/event-placeholder.jpg';

const formatMediaItems = (event: Event): MediaItem[] => {
  const mediaItems: MediaItem[] = [];
  const fallbackImage = '/images/event-placeholder.jpg';
  
  // Helper function to validate image URL
  const isValidImageUrl = (url: string | undefined): url is string => {
    return Boolean(url && typeof url === 'string' && url.trim().length > 0);
  };
  
  // Add cover image if it exists and is valid
  if (isValidImageUrl(event.coverImage)) {
    mediaItems.push({
      id: 'cover',
      type: 'image',
      url: event.coverImage,
      alt: `${event.title} cover image`
    });
  }

  // Add media images if they exist
  if (Array.isArray(event.media)) {
    event.media.forEach((item, index) => {
      if (typeof item === 'string' && isValidImageUrl(item)) {
        mediaItems.push({
          id: `media-${index}`,
          type: 'image',
          url: item,
          alt: `${event.title} image ${index + 1}`
        });
      } else if (typeof item === 'object' && item !== null && isValidImageUrl(item.url)) {
        mediaItems.push({
          ...item,
          id: item.id || `media-${index}`,
          alt: item.alt || `${event.title} image ${index + 1}`
        });
      }
    });
  }

  // If no valid media items were found, add a fallback image --sobhi
  if (mediaItems.length === 0) {
    mediaItems.push({
      id: 'fallback',
      type: 'image',
      url: fallbackImage,
      alt: `${event.title} placeholder image`
    });
  }

  return mediaItems;
};

const SmartImageSlideshow: React.FC<{ images: string[] }> = ({ images }) => {
  if (!images || images.length === 0) {
    return (
      <div className="aspect-video flex items-center justify-center bg-gray-100 rounded-lg border border-slate-200">
        <span className="text-gray-400">No image available</span>
      </div>
    );
  }
  if (images.length === 1) {
    return (
      <img
        src={images[0]}
        alt="Event"
        className="w-full aspect-video object-cover rounded-lg"
        onError={e => (e.currentTarget.src = '/images/event-placeholder.jpg')}
      />
    );
  }
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    swipe: true,
    adaptiveHeight: true,
  };
  return (
    <Slider {...settings}>
      {images.map((url, idx) => (
        <div key={idx}>
          <img
            src={url}
            alt={`Event image ${idx + 1}`}
            className="w-full aspect-video object-cover rounded-lg"
            onError={e => (e.currentTarget.src = '/images/event-placeholder.jpg')}
          />
        </div>
      ))}
    </Slider>
  );
};

const EventHighlights: React.FC<{ event: Event }> = ({ event }) => {
  if (!event) {
    return null; // Return nothing if there's no event
  }
  
  // Use the utility functions for safer access to ticket tiers
  const ticketTiers = getTicketTiersArray(event);
  
  // If there are no valid ticket tiers, show simplified stats
  if (ticketTiers.length === 0) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="p-4 bg-gray-100 rounded-lg text-center">
          <p>No ticket information available</p>
        </div>
      </div>
    );
  }
  
  const stats = [
    {
      icon: FiUsers,
      label: "Total Capacity",
      value: ticketTiers.reduce((sum, tier) => sum + tier.quantity, 0).toLocaleString()
    },
    {
      icon: FiStar,
      label: "Category",
      value: event.category
    },
    {
      icon: FiTrendingUp,
      label: "Ticket Status",
      value: ticketTiers.some(tier => 
        (tier.sold || 0) > (tier.quantity * 0.7)
      ) ? "High Demand" : "Available",
      color: ticketTiers.some(tier => 
        (tier.sold || 0) > (tier.quantity * 0.7)
      ) ? "text-red-500" : "text-green-500"
    }
  ];

  const minPrice = getMinTicketPrice(event) || 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <Card className="overflow-hidden hover:shadow-md transition-all duration-200 bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={cn(
                  "p-3 rounded-xl",
                  stat.color || "bg-primary/10 text-primary"
                )}>
                  <stat.icon className="h-6 w-6" />
        </div>
        <div>
                  <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
                  <p className="text-lg font-semibold mt-1">{stat.value}</p>
        </div>
        </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
};

const EventFAQs: React.FC<{ event: Event }> = ({ event }) => {
  const defaultFAQs = [
    {
      question: "What's the refund policy?",
      answer: "Refunds are available up to 24 hours before the event starts. Contact the organizer for more information."
    },
    {
      question: "Is my ticket transferable?",
      answer: "Yes, you can transfer your ticket to another person by updating the attendee information in your booking."
    },
    {
      question: "What happens if the event is cancelled?",
      answer: "In case of cancellation, you will receive a full refund automatically."
    }
  ];

    return (
    <Card className="bg-white shadow-sm hover:shadow-md transition-all duration-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <FiHelpCircle className="w-5 h-5 text-primary" />
          Frequently Asked Questions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {defaultFAQs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={cn(
                "pb-6",
                index !== defaultFAQs.length - 1 && "border-b border-slate-100"
              )}
            >
              <h4 className="font-medium text-lg mb-2">{faq.question}</h4>
              <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
            </motion.div>
          ))}
      </div>
      </CardContent>
    </Card>
  );
};

const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  try {
    // Use OpenStreetMap Nominatim API for geocoding
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`;
    const response = await axios.get(url);
    if (response.data && response.data.length > 0) {
      return {
        lat: parseFloat(response.data[0].lat),
        lng: parseFloat(response.data[0].lon),
      };
    }
    return null;
  } catch (err) {
    return null;
  }
};

const LocationSection: React.FC<{ event: Event }> = ({ event }) => {
  const [mapError, setMapError] = useState<string | null>(null);
  const [geoCoords, setGeoCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const { location } = event;

  const hasValidCoordinates = location?.coordinates?.lat && location?.coordinates?.lng;

  const handleMapError = () => {
    setMapError('Unable to load map');
  };

  // Compose address string using location properties
  const addressString = [
    location?.venue,
    location?.address,
    location?.city,
    location?.postcode
  ].filter(Boolean).join(', ');

  useEffect(() => {
    if (!hasValidCoordinates && addressString) {
      setLoading(true);
      geocodeAddress(addressString).then(coords => {
        setGeoCoords(coords);
        setLoading(false);
      });
    }
  }, [hasValidCoordinates, addressString]);

  // Memoize center for SimpleMap
  const mapCenter = useMemo(() => {
    if (hasValidCoordinates) {
      return { lat: location.coordinates.lat, lng: location.coordinates.lng };
    } else if (geoCoords) {
      return geoCoords;
    }
    return null;
  }, [hasValidCoordinates, location?.coordinates?.lat, location?.coordinates?.lng, geoCoords]);

  return (
    <Card className="overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200">
      <CardHeader className="pb-0">
        <CardTitle className="flex items-center gap-2 text-xl">
          <div className="p-2 rounded-full bg-primary/10">
            <FiMapPin className="w-5 h-5 text-primary" />
          </div>
          Location
        </CardTitle>
        {addressString && (
          <div className="mt-2 px-1 sm:px-2">
            <h2 className="text-sm sm:text-base font-normal text-gray-500 truncate">{addressString}</h2>
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        <div className="relative rounded-xl overflow-hidden border border-slate-200" style={{ height: "400px" }}>
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
              <span className="text-gray-500 text-sm">Locating address...</span>
            </div>
          ) : mapCenter ? (
            <SimpleMap
              center={mapCenter}
              zoom={15}
              onError={handleMapError}
              showLocationIndicator={true}
              className="w-full h-full"
              mapStyle="light"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
              <p className="text-gray-500 text-sm">Location map not available</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

interface TicketTiersSectionProps {
  tiers: TicketTier[];
  onTierSelect: (tierId: string) => void;
  onQuantityChange: (quantity: number) => void;
  selectedTierId: string | null;
  selectedQuantity: number;
  onBookingStart: (tierId: string, quantity: number) => void;
}

const TicketTiersSection: React.FC<TicketTiersSectionProps> = ({
  tiers,
  onTierSelect,
  onQuantityChange,
  selectedTierId,
  selectedQuantity,
  onBookingStart
}) => {
  // Always ensure tiers is an array
  const validTiers = Array.isArray(tiers) ? tiers : [];
  
  if (validTiers.length === 0) {
    return (
      <div className="p-4 bg-gray-100 rounded-lg text-center">
        <p>No tickets available for this event.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {validTiers.map((tier) => {
        const isSoldOut = tier.quantity <= (tier.sold || 0);
        const tierId = tier.id || '';
        
        return (
          <div
            key={tierId}
            className={`p-4 border rounded-lg ${
              selectedTierId === tierId ? 'border-primary bg-primary/5' : 'border-gray-200'
            } ${isSoldOut ? 'opacity-60' : ''} cursor-pointer hover:border-primary hover:bg-primary/5 transition-all duration-200`}
            onClick={() => !isSoldOut && onTierSelect(tierId)}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium">{tier.name}</h4>
                {tier.description && (
                  <p className="text-sm text-gray-500 mt-1">{tier.description}</p>
                )}
              </div>
              <div className="text-right">
                <div className="font-bold">${(tier.price || 0).toFixed(2)}</div>
                <div className={`text-sm ${isSoldOut ? 'text-red-500' : 'text-gray-500'}`}>
                  {isSoldOut ? 'Sold Out' : `${Math.max(0, (tier.quantity || 0) - (tier.sold || 0))} remaining`}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-2">
              {!isSoldOut && (
                <button
                  type="button"
                  className={`px-3 py-1 text-sm rounded-md ${
                    selectedTierId === tierId
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-primary/10'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTierSelect(tierId);
                  }}
                >
                  {selectedTierId === tierId ? 'Selected' : 'Select'}
                </button>
              )}
            </div>
          </div>
        );
      })}
      
      {selectedTierId && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
            <div>
              <div className="text-sm text-gray-500">Quantity</div>
              <div className="flex items-center mt-1">
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-full border bg-white"
                  onClick={() => onQuantityChange(Math.max(1, selectedQuantity - 1))}
                  disabled={selectedQuantity <= 1}
                >
                  -
                </button>
                <span className="mx-4 font-medium">{selectedQuantity}</span>
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-full border bg-white"
                  onClick={() => {
                    const selectedTier = validTiers.find(t => t.id === selectedTierId);
                    if (selectedTier) {
                      const maxQuantity = selectedTier.quantity - (selectedTier.sold || 0);
                      onQuantityChange(Math.min(maxQuantity, selectedQuantity + 1));
                    }
                  }}
                >
                  +
                </button>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Total</div>
              <div className="text-xl font-bold">
                ${((validTiers.find(t => t.id === selectedTierId)?.price || 0) * selectedQuantity).toFixed(2)}
              </div>
            </div>
          </div>
          
          <button
            type="button"
            className="w-full py-3 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark font-semibold text-lg flex items-center justify-center gap-2"
            onClick={() => onBookingStart(selectedTierId, selectedQuantity)}
          >
            <FiCalendar className="w-5 h-5" />
            Continue to Booking
          </button>
        </div>
      )}
    </div>
  );
};

const OrganizerInfo: React.FC<{ event: Event }> = ({ event }) => {
  const { user } = useAuth();
  const [organizerData, setOrganizerData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchOrganizerData = async () => {
  if (!event.organizerId) {
        setLoading(false);
        return;
      }

      try {
        const organizerDoc = await getDoc(doc(db, 'users', event.organizerId));
        if (organizerDoc.exists()) {
          const data = organizerDoc.data();
          // Fetch event count
          const eventsRef = collection(db, 'events');
          const eventsQuery = query(
            eventsRef,
            where('organizerId', '==', event.organizerId),
            where('status', '==', 'published')
          );
          const querySnapshot = await getDocs(eventsQuery);
          data.eventsHosted = querySnapshot.size;
          setOrganizerData(data);
        }
      } catch (error) {
        console.error('Error fetching organizer data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizerData();
  }, [event.organizerId]);

  const handleContact = () => {
    toast.info('Contact functionality coming soon!');
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!event.organizerId || loading) {
  return (
      <Card className="animate-pulse bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <div className="p-2 rounded-full bg-primary/10">
              <FiUser className="w-5 h-5 text-primary" />
            </div>
            Organized by
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
      <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full" />
            <div className="space-y-2 flex-1">
              <div className="h-5 w-32 bg-gray-200 rounded" />
              <div className="h-4 w-40 bg-gray-200 rounded" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayName = organizerData?.displayName || organizerData?.name || event.organizerName || 'Anonymous Organizer';
  const eventsHosted = organizerData?.eventsHosted || 0;

  return (
    <Card className="bg-white shadow-sm hover:shadow-md transition-all duration-200">
      <CardHeader className="pb-0">
        <CardTitle className="flex items-center gap-2 text-xl">
          <div className="p-2 rounded-full bg-primary/10">
            <FiUser className="w-5 h-5 text-primary" />
          </div>
          Organized by
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          {imageError || !organizerData?.photoURL ? (
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-medium border-2 border-white shadow-sm">
              {getInitials(displayName)}
            </div>
          ) : (
            <img
              src={organizerData.photoURL}
              alt={displayName}
              className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
          onError={handleImageError}
        />
          )}
          
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{displayName}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <FiCalendar className="w-4 h-4" />
              <span>{eventsHosted} events hosted</span>
            </div>
            <div className="flex items-center gap-2">
              {user?.uid !== event.organizerId && (
                <Button 
                  variant="default"
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  onClick={handleContact}
                >
                  Contact Organizer
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface EventState {
  event: Event | null;
  selectedTierId: string | null;
  selectedQuantity: number;
  isProcessing: boolean;
  loading: boolean;
  error: string | null;
}

interface ExtTicketTier extends TicketTier {
  price?: number;
  name?: string;
  id?: string;
}

interface BookingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (attendeeInfo: Array<{name: string; email: string; phone?: string}>) => void;
  event: Event;
  ticketTier: ExtTicketTier | null;
  quantity: number;
  isProcessing: boolean;
}

const BookingDialog: React.FC<BookingDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  event,
  ticketTier,
  quantity,
  isProcessing
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalPrice = ticketTier ? (ticketTier.price || 0) * quantity : 0;
  const [attendeeInfo, setAttendeeInfo] = useState<Array<{name: string; email: string; phone?: string}>>([]);
  const [agreement, setAgreement] = useState(false);
  
  // Initialize attendee info when quantity changes
  useEffect(() => {
    const newAttendeeInfo = [];
    for (let i = 0; i < quantity; i++) {
      newAttendeeInfo.push({
        name: attendeeInfo[i]?.name || '',
        email: attendeeInfo[i]?.email || '',
        phone: attendeeInfo[i]?.phone || '',
      });
    }
    setAttendeeInfo(newAttendeeInfo);
  }, [quantity]);

  // Reset dialog state when closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setAgreement(false);
    }
  }, [isOpen]);
  
  const formatDate = (date: Timestamp | Date | string): string => {
    if (!date) return 'Date not set';
    try {
      if (date instanceof Timestamp) {
        return format(date.toDate(), 'PPP p');
      } else if (typeof date === 'string') {
        return format(parseISO(date), 'PPP p');
      }
      return format(date, 'PPP p');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };
  
  // Handle input change for attendee info
  const handleAttendeeChange = (index: number, field: string, value: string) => {
    const newAttendeeInfo = [...attendeeInfo];
    newAttendeeInfo[index] = {
      ...newAttendeeInfo[index],
      [field]: value || ''
    };
    setAttendeeInfo(newAttendeeInfo);
  };
  
  const handleNextStep = () => {
    if (currentStep === 1) {
      // No validation needed for step 1 (review)
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === 2) {
      // Validate attendee information
      const isValid = attendeeInfo.every(info => info.name.trim() && info.email.trim());
      if (!isValid) {
        toast.error('Please fill in name and email for all attendees');
        return;
      }
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === 3) {
      // Validate agreement
      if (!agreement) {
        toast.error('Please agree to the terms and conditions');
        return;
      }
      // Submit the form
      onConfirm(attendeeInfo);
    }
  };
  
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      onClose();
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Your Booking</DialogTitle>
          <DialogDescription>
            {currentStep === 1 ? 'Review your ticket selection' : 
             currentStep === 2 ? 'Enter attendee information' : 
             'Confirm and complete payment'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-2">
          <div className="flex justify-between mb-4">
            {[1, 2, 3].map(step => (
              <div key={step} className="flex flex-col items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step < currentStep ? 'bg-green-500 text-white' :
                    step === currentStep ? 'bg-primary text-white' : 
                    'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step < currentStep ? (
                    <FiCheck className="h-4 w-4" />
                  ) : (
                    <span>{step}</span>
                  )}
    </div>
                <span className="text-xs mt-1 text-gray-500">
                  {step === 1 ? 'Review' : step === 2 ? 'Attendees' : 'Payment'}
                </span>
              </div>
            ))}
          </div>
          
          <Separator className="my-4" />
          
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-medium text-lg">{event.title}</h3>
                <p className="text-gray-500 text-sm mt-1">{formatDate(event.startDateTime)}</p>
                <p className="text-gray-500 text-sm mt-1">
                  {event.location?.address || 'Location not specified'}
                </p>
              </div>
              
              <div className="border rounded-md p-4">
                <div className="flex justify-between">
        <div>
                    <p className="font-medium">{ticketTier?.name}</p>
                    <p className="text-sm text-gray-500 mt-1">${(ticketTier?.price || 0).toFixed(2)} × {quantity}</p>
        </div>
                  <p className="font-bold">${(totalPrice || 0).toFixed(2)}</p>
      </div>
    </div>
            </div>
          )}
          
          {currentStep === 2 && (
            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
              <p className="text-sm text-gray-500">
                Please provide details for each attendee. This information is required for event check-in.
              </p>
              
              {attendeeInfo.map((attendee, index) => (
                <div key={index} className="space-y-3 pb-4 border-b">
                  <h4 className="font-medium text-sm">Attendee {index + 1}</h4>
                  
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={attendee.name}
                        onChange={(e) => handleAttendeeChange(index, 'name', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={attendee.email}
                        onChange={(e) => handleAttendeeChange(index, 'email', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Phone Number (optional)
                      </label>
                      <input
                        type="tel"
                        value={attendee.phone}
                        onChange={(e) => handleAttendeeChange(index, 'phone', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="border rounded-md p-4">
                <h4 className="font-medium mb-3">Booking Summary</h4>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Event</span>
                    <span>{event.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date</span>
                    <span>{formatDate(event.startDateTime)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ticket Type</span>
                    <span>{ticketTier?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Quantity</span>
                    <span>{quantity}</span>
                  </div>
                  
                  <Separator className="my-2" />
                  
                  <div className="flex justify-between font-medium">
                    <span>Total</span>
                    <span>${(totalPrice || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-primary-50 border border-primary-100 rounded-md p-4">
                <h4 className="font-medium text-primary-900 flex items-center">
                  <FiCreditCard className="mr-2 h-4 w-4" />
                  Payment Information
                </h4>
                <p className="text-sm text-primary-700 mt-2">
                  In this demo version, no actual payment is processed.
                  Click "Complete Booking" to simulate a successful transaction.
                </p>
              </div>
              
              <div className="mt-4">
                <label className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked={agreement}
                    onChange={(e) => setAgreement(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the terms and conditions, including the refund policy and privacy policy.
                    I acknowledge that my booking is subject to availability and confirmation.
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex sm:justify-between gap-2">
          <Button variant="outline" onClick={handlePrevStep} disabled={isProcessing}>
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>
          <Button onClick={handleNextStep} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <span className="animate-spin mr-2">
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </span>
                Processing...
              </>
            ) : (
              currentStep === 3 ? 'Complete Booking' : 'Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ShareDialog: React.FC<{ isOpen: boolean; onClose: () => void; event: Event }> = ({
  isOpen,
  onClose,
  event
}) => {
  const shareUrl = window.location.href;
  const shareText = `Check out ${event.title} on Eventory!`;

  const shareOptions = [
    {
      name: 'Copy Link',
      icon: FiLink,
      onClick: () => {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard');
        onClose();
      }
    },
    {
      name: 'Share on Twitter',
      icon: FiTwitter,
      onClick: () => {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank'
        );
      }
    },
    {
      name: 'Share on Facebook',
      icon: FiFacebook,
      onClick: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
          '_blank'
        );
      }
    },
    {
      name: 'Share on LinkedIn',
      icon: FiLinkedin,
      onClick: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          '_blank'
        );
      }
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share Event</DialogTitle>
          <DialogDescription>
            Share this event with your friends and network
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {shareOptions.map((option) => (
            <Button
              key={option.name}
              variant="outline"
              className="flex items-center gap-2"
              onClick={option.onClick}
            >
              <option.icon className="h-4 w-4" />
              {option.name}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EventInfo: React.FC<{ event: Event }> = ({ event }) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [showReadMore, setShowReadMore] = useState(false);

  useEffect(() => {
    if (descriptionRef.current) {
      setShowReadMore(
        descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight
      );
    }
  }, [event.description]);

  const formatEventTime = (date: Timestamp | Date | string): string => {
    if (!date) return '';
    return formatTime(date);
  };

  const getEventTiming = () => {
    if (!event.startDateTime || !event.endDateTime) return 'Dates not set';

    let start: Date | Timestamp;
    let end: Date | Timestamp;

    try {
      // Handle different date formats
      if (event.startDateTime instanceof Timestamp) {
        start = event.startDateTime;
      } else if (typeof event.startDateTime === 'string') {
        start = new Date(event.startDateTime);
      } else if (event.startDateTime instanceof Date) {
        start = event.startDateTime;
      } else {
        console.error('Invalid startDateTime format:', event.startDateTime);
        return 'Invalid date format';
      }

      if (event.endDateTime instanceof Timestamp) {
        end = event.endDateTime;
      } else if (typeof event.endDateTime === 'string') {
        end = new Date(event.endDateTime);
      } else if (event.endDateTime instanceof Date) {
        end = event.endDateTime;
      } else {
        console.error('Invalid endDateTime format:', event.endDateTime);
        return 'Invalid date format';
      }

      // Validate dates
      if ((start instanceof Date && isNaN(start.getTime())) || 
          (end instanceof Date && isNaN(end.getTime()))) {
        console.error('Invalid date values:', { start, end });
        return 'Invalid date values';
      }

      const startDate = formatDate(start);
      const endDate = formatDate(end);
      const startTime = formatTime(start);
      const endTime = formatTime(end);
      
      if (startDate === endDate) {
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FiCalendar className="w-5 h-5 text-primary" />
              <span className="font-medium">{startDate}</span>
            </div>
            <div className="flex items-center gap-2 ml-7">
              <FiClock className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {startTime} - {endTime}
              </span>
            </div>
          </div>
        );
      }
      
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FiCalendar className="w-5 h-5 text-primary" />
              <span className="font-medium">Starts</span>
            </div>
            <div className="flex flex-col ml-7">
              <span>{startDate}</span>
              <span className="text-muted-foreground">{startTime}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FiCalendar className="w-5 h-5 text-primary" />
              <span className="font-medium">Ends</span>
            </div>
            <div className="flex flex-col ml-7">
              <span>{endDate}</span>
              <span className="text-muted-foreground">{endTime}</span>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error formatting event dates:', error);
      return 'Error displaying event dates';
    }
  };

  const getTotalCapacity = () => {
    const ticketTiers = getTicketTiersArray(event);
    return ticketTiers.reduce((sum, tier) => sum + tier.quantity, 0);
  };

  const getSoldTickets = () => {
    const ticketTiers = getTicketTiersArray(event);
    return ticketTiers.reduce((sum, tier) => sum + (tier.sold || 0), 0);
  };

  const getAvailableTickets = () => {
    return getTotalCapacity() - getSoldTickets();
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'published':
      return 'bg-green-100 text-green-800';
      case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

  const formatStatus = (status: string | undefined) => {
  if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  return (
    <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <FiInfo className="w-5 h-5 text-primary" />
          About this event
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-50 rounded-xl p-6 border border-slate-100"
        >
          {getEventTiming()}
        </motion.div>
        
        <div className="space-y-4">
          <div className="prose max-w-none">
            <div 
              ref={descriptionRef}
              className={cn(
                "text-gray-600 text-base leading-relaxed whitespace-pre-wrap",
                !isDescriptionExpanded && "line-clamp-3"
              )}
            >
              <DescriptionRenderer text={event.description || ''} />
            </div>
            {showReadMore && (
              <button
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className="text-primary hover:text-primary/90 text-sm font-medium mt-3 flex items-center gap-1 group transition-colors"
              >
                {isDescriptionExpanded ? "Show less" : "Read more"}
                <motion.span
                  animate={{ rotate: isDescriptionExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isDescriptionExpanded ? (
                    <FiChevronUp className="w-4 h-4 group-hover:transform group-hover:-translate-y-0.5 transition-transform" />
                  ) : (
                    <FiChevronDown className="w-4 h-4 group-hover:transform group-hover:translate-y-0.5 transition-transform" />
                  )}
                </motion.span>
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const getEventImages = (event: Event): string[] => {
  const images: string[] = [];
  if (event.coverImage) images.push(event.coverImage);
  if (Array.isArray(event.media)) {
    (event.media as MediaItem[]).forEach((item, idx) => {
      if (item.type === 'image' && item.url) images.push(item.url);
    });
  }
  // Remove duplicates
  return Array.from(new Set(images));
};

const ErrorFallback = ({ error }: { error: Error }) => {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
      <h3 className="text-lg font-semibold mb-2">Something went wrong</h3>
      <p className="text-sm">{error.message}</p>
    </div>
  );
};

const MoreEventsByOrganizer: React.FC<{ organizerId: string; currentEventId: string }> = ({ organizerId, currentEventId }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrganizerEvents = async () => {
      try {
        const eventsRef = collection(db, 'events');
        const q = query(
          eventsRef,
          where('organizerId', '==', organizerId),
          where('status', '==', 'published')
        );
        const querySnapshot = await getDocs(q);
        const eventsData = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as Event))
          .filter(event => event.id !== currentEventId) // Filter out the current event on the client-side
          .sort((a, b) => {
            // Sort by start date, most recent first
            const dateA = a.startDateTime instanceof Timestamp ? a.startDateTime.toDate() : new Date(a.startDateTime);
            const dateB = b.startDateTime instanceof Timestamp ? b.startDateTime.toDate() : new Date(b.startDateTime);
            return dateB.getTime() - dateA.getTime();
          });
        setEvents(eventsData);
      } catch (error) {
        console.error('Error fetching organizer events:', error);
      } finally {
        setLoading(false);
      }
    };

    if (organizerId) {
      fetchOrganizerEvents();
    }
  }, [organizerId, currentEventId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">More by this organizer</h3>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="w-64 flex-shrink-0">
              <Card className="h-full">
                <CardContent className="p-4">
                  <div className="animate-pulse space-y-3">
                    <div className="h-32 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold">More by this organizer</h3>
      <div className="relative">
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={16}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          breakpoints={{
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
          }}
          className="pb-8"
        >
          {events.map((event) => (
            <SwiperSlide key={event.id}>
              <div className="h-full">
                <EventCard event={event} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<EventState>({
    event: null,
    loading: true,
    error: null,
    selectedTierId: null,
    selectedQuantity: 1,
    isProcessing: false
  });
  const [isSaved, setIsSaved] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const initialEvent = location.state?.event as Event | null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        if (!id) throw new Error('Event ID is required');
        const eventData = await getEventById(id);
        setState({ ...state, event: eventData as any, loading: false, error: null });
      } catch (err) {
        setState(prev => ({
          ...prev,
          error: err instanceof Error ? err.message : 'Failed to load event',
          loading: false
        }));
        toast.error('Failed to load event');
      }
    };

    fetchEvent();
    checkIfSaved();
  }, [id]);

  const checkIfSaved = useCallback(async () => {
    if (!user || !id) return;

    try {
      const saveDoc = await getDoc(doc(db, 'saves', `${user.uid}_${id}`));
      setIsSaved(saveDoc.exists());
    } catch (error) {
      console.error('Error checking save status:', error);
      // Don't show an error toast - just fail silently
      // The save functionality will still work
    }
  }, [user, id]);

  const handleSaveEvent = async () => {
    if (!user) {
      toast.error('Please log in to save events');
      navigate('/login', { 
        state: { from: location.pathname },
        replace: true 
      });
      return;
    }

    if (!state.event || !id) return;

    setState(prev => ({ ...prev, isProcessing: true }));
    const saveId = `${user.uid}_${id}`;
    const saveRef = doc(db, 'saves', saveId);

    try {
      if (isSaved) {
        await deleteDoc(saveRef);
        setIsSaved(false);
        toast.success('Event removed from saved items');
      } else {
        // Get the best available image URL
        let imageUrl = null;
        if (state.event.imageUrl) {
          imageUrl = state.event.imageUrl;
        } else if (state.event.coverImage) {
          imageUrl = state.event.coverImage;
        } else if (state.event.media && Array.isArray(state.event.media.images) && state.event.media.images.length > 0) {
            imageUrl = state.event.media.images[0];
        }

        // Format the location string
        const locationStr = state.event.location ? 
          [state.event.location.venue, state.event.location.city, state.event.location.state]
            .filter(Boolean)
            .join(', ') : '';

        // Ensure all required fields are defined with fallbacks
        const saveData = {
          userId: user.uid,
          eventId: id,
          eventTitle: state.event.title || 'Untitled Event',
          eventImage: imageUrl || '/placeholder-event.jpg',
          eventDate: state.event.startDateTime || serverTimestamp(),
          savedAt: serverTimestamp(),
          eventStatus: state.event.status || 'draft',
          eventCategory: state.event.category || 'uncategorized',
          eventLocation: locationStr || 'Location TBA',
          organizerId: state.event.organizerId || user.uid,
          ticketTiers: getTicketTiersArray(state.event).map(tier => ({
            id: tier.id || crypto.randomUUID(),
            name: tier.name,
            price: tier.price,
            quantity: tier.quantity
          }))
        };
        
        await setDoc(saveRef, saveData);
        setIsSaved(true);
        toast.success('Event saved successfully');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save event');
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const getEventStatus = (startDateTime: Timestamp, endDateTime: Timestamp): EventStatus => {
    try {
      if (!startDateTime) return 'draft';
      
      // Safely convert to Date objects
      const start = startDateTime instanceof Timestamp 
        ? startDateTime.toDate() 
        : new Date(startDateTime);
        
      const end = endDateTime instanceof Timestamp 
        ? endDateTime.toDate() 
        : new Date(endDateTime);
        
      const now = new Date();

      // Make sure dates are valid before comparison
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.error('Invalid date in getEventStatus:', { start, end });
        return 'upcoming'; // Default to upcoming if dates are invalid
      }

      if (now > end) return 'completed';
      if (now >= start && now <= end) return 'ongoing';
      return 'upcoming';
    } catch (error) {
      console.error('Error in getEventStatus:', error);
      return 'upcoming'; // Default to upcoming on error
    }
  };

  const triggerBookingSuccess = () => {
    toast.success("Booking Successful!");
  };

  const handleBookingConfirmed = () => {
    setShowBookingSuccess(true);
      setTimeout(() => {
      setShowBookingSuccess(false);
      navigate('/bookings');
    }, 3000);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (state.error || !state.event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{state.error || 'Event not found'}</AlertDescription>
        </Alert>
        <button
          onClick={() => navigate('/events')}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Back to Events
        </button>
      </div>
    );
  }

  const { event } = state;

  return (
    <div className={cn("w-full max-w-full px-2 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 mx-auto")}>
      <div className={cn("grid gap-8 lg:grid-cols-[2fr,1fr]", isMobile && "event-details-grid")}>
        {isMobile ? (
          <>
            <aside className="order-first mb-8 lg:order-none lg:mb-0">
              <div className="sticky top-8">
                <EventBookingDisplay event={event} />
              </div>
            </aside>
            <div className="space-y-8">
              <section aria-label="Event media" className={cn("rounded-2xl overflow-hidden bg-white shadow-sm", isMobile && "event-details-media")}>
                <ErrorBoundary 
                  FallbackComponent={ErrorFallback}
                >
                  <Suspense 
                    fallback={
                      <div className="relative aspect-video w-full animate-pulse rounded-lg bg-slate-100">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
                        </div>
                      </div>
                    }
                  >
                    <SmartImageSlideshow images={getEventImages(event)} />
                  </Suspense>
                </ErrorBoundary>
              </section>

              <section aria-label="Event information" className={cn("space-y-6", isMobile && "event-details-section")}>
                <div className="flex justify-between items-start">
                  <div className="space-y-4">
                    <h1 className={cn("text-4xl font-bold tracking-tight text-gray-900", isMobile && "event-details-title")}>{event.title}</h1>
                    <div className="flex items-center gap-3 text-sm">
                      <Badge 
                        variant="secondary" 
                        className="bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        {event.category}
                      </Badge>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600">{typeof event.startDateTime === 'string' 
                        ? formatDate(new Date(event.startDateTime)) 
                        : formatDate(event.startDateTime)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {user && user.uid === event.organizerId && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={() => navigate(`/events/${id}/edit`)}
                      >
                        <FiEdit2 className="w-4 h-4" />
                        Edit Event
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                      onClick={() => setIsShareDialogOpen(true)}
                    >
                      <FiShare2 className="w-4 h-4" />
                      Share
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "flex items-center gap-2",
                        isSaved && "text-primary border-primary hover:bg-primary/10"
                      )}
                      onClick={handleSaveEvent}
                      disabled={state.isProcessing}
                    >
                      {state.isProcessing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FiHeart className={cn("w-4 h-4", isSaved && "fill-primary")} />
                      )}
                      {isSaved ? "Saved" : "Save"}
                    </Button>
                  </div>
                </div>

                <EventInfo event={event} />
              </section>

              <div className={cn("space-y-8", isMobile && "event-details-section")}>
                <EventHighlights event={event} />
                <LocationSection event={event} />
                <OrganizerInfo event={event} />
                <EventFAQs event={event} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-8">
              <section aria-label="Event media" className={cn("rounded-2xl overflow-hidden bg-white shadow-sm", isMobile && "event-details-media")}>
                <ErrorBoundary 
                  FallbackComponent={ErrorFallback}
                >
                  <Suspense 
                    fallback={
                      <div className="relative aspect-video w-full animate-pulse rounded-lg bg-slate-100">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
                        </div>
                      </div>
                    }
                  >
                    <SmartImageSlideshow images={getEventImages(event)} />
                  </Suspense>
                </ErrorBoundary>
              </section>

              <section aria-label="Event information" className={cn("space-y-6", isMobile && "event-details-section")}>
                <div className="flex justify-between items-start">
                  <div className="space-y-4">
                    <h1 className={cn("text-4xl font-bold tracking-tight text-gray-900", isMobile && "event-details-title")}>{event.title}</h1>
                    <div className="flex items-center gap-3 text-sm">
                      <Badge 
                        variant="secondary" 
                        className="bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        {event.category}
                      </Badge>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600">{typeof event.startDateTime === 'string' 
                        ? formatDate(new Date(event.startDateTime)) 
                        : formatDate(event.startDateTime)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {user && user.uid === event.organizerId && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={() => navigate(`/events/${id}/edit`)}
                      >
                        <FiEdit2 className="w-4 h-4" />
                        Edit Event
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                      onClick={() => setIsShareDialogOpen(true)}
                    >
                      <FiShare2 className="w-4 h-4" />
                      Share
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "flex items-center gap-2",
                        isSaved && "text-primary border-primary hover:bg-primary/10"
                      )}
                      onClick={handleSaveEvent}
                      disabled={state.isProcessing}
                    >
                      {state.isProcessing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FiHeart className={cn("w-4 h-4", isSaved && "fill-primary")} />
                      )}
                      {isSaved ? "Saved" : "Save"}
                    </Button>
                  </div>
                </div>

                <EventInfo event={event} />
              </section>

              <div className={cn("space-y-8", isMobile && "event-details-section")}>
                <EventHighlights event={event} />
                <LocationSection event={event} />
                <OrganizerInfo event={event} />
                <EventFAQs event={event} />
              </div>
            </div>
            <aside className="order-first mb-8 lg:order-none lg:mb-0">
              <div className="sticky top-8">
                <EventBookingDisplay event={event} />
              </div>
            </aside>
          </>
        )}
      </div>
      
      <ShareDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        event={event}
      />
      
      <BookingSuccessDialog
        isOpen={showBookingSuccess}
        onClose={() => {
          setShowBookingSuccess(false);
          navigate('/bookings');
        }}
        event={event}
      />

      {event && (
        <div className={cn("mt-24", isMobile && "event-details-more-events")}>
          <MoreEventsByOrganizer 
            organizerId={event.organizerId} 
            currentEventId={event.id} 
          />
        </div>
      )}
    </div>
  );
};

// Update the EventBookingDisplay component to revert to the original design
const EventBookingDisplay = ({ event }: { event: Event }) => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<ExtTicketTier | null>(null);
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if tickets are available
  const isTicketsAvailable = hasAvailableTickets(event);
  
  // Use our new utility function
  const isPastEvent = isEventPast(event);

  const handleTierSelect = (tierId: string) => {
    setSelectedTierId(tierId);
    const tier = getTicketTiersArray(event).find(t => t.id === tierId);
    if (tier) {
      setSelectedTier(tier as ExtTicketTier);
    }
  };

  const handleQuantityChange = (qty: number) => {
    setQuantity(qty);
  };

  const handleBookingStart = (tierId: string, qty: number) => {
    if (!user) {
      toast.error('You must be logged in to book tickets');
      navigate('/login', { state: { from: `/events/${event.id}` } });
      return;
    }
    
    const tier = getTicketTiersArray(event).find(t => t.id === tierId);
    if (!tier) {
      toast.error('Please select a ticket type');
      return;
    }
    
    setSelectedTier(tier as ExtTicketTier);
    setQuantity(qty);
    setShowBookingModal(true);
  };

  // Add early return if no event is provided
  if (!event) {
    return null;
  }
  
  // Get ticket tiers safely
  const ticketTiers = getTicketTiersArray(event);
  
  // Use yet another variable name to avoid conflicts
  const hasAvailableTicketTiers = hasAvailableTickets(event);
  
  return (
    <div className="bg-white rounded-3xl shadow-lg border border-slate-200 overflow-hidden transition-all duration-500">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Book Your Tickets</h2>
        
        <p className="text-gray-600 mb-4">
          Select a ticket type below to book your spot for this event.
        </p>
        
        {isPastEvent ? (
          <div className="text-center py-8">
            <FiAlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">This event has already taken place</h3>
            <p className="mt-1 text-gray-500">Check our other upcoming events</p>
            <Button 
              className="mt-6" 
              onClick={() => navigate('/events')}
            >
              Explore Events
            </Button>
          </div>
        ) : !hasAvailableTicketTiers ? (
          <div className="text-center py-8">
            <FiAlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">Sold Out</h3>
            <p className="mt-1 text-gray-500">Sorry, tickets are no longer available for this event</p>
            <Button 
              className="mt-6" 
              onClick={() => navigate('/events')}
            >
              Explore Other Events
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {event && getTicketTiersArray(event).length > 0 && (
                <TicketTiersSection
                  tiers={getTicketTiersArray(event)}
                  onTierSelect={handleTierSelect}
                  onQuantityChange={handleQuantityChange}
                  selectedTierId={selectedTierId}
                  selectedQuantity={quantity}
                  onBookingStart={handleBookingStart}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Booking Dialog */}
      <BookingDialog 
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        onConfirm={async (attendeeInfo) => {
          if (!selectedTier || !user) return;
          try {
            const bookingData = {
              eventId: event.id,
              userId: user.uid,
              ticketTierId: selectedTier.id,
              ticketTierName: selectedTier.name,
              quantity,
              totalPrice: (selectedTier.price || 0) * quantity,
              status: 'confirmed',
              paymentStatus: 'paid',
              attendeeInfo,
              createdAt: new Date(),
              updatedAt: new Date(),
              paymentDetails: {
                method: 'credit_card',
                transactionId: crypto.randomUUID(),
                amount: (selectedTier.price || 0) * quantity,
                currency: 'USD',
                paidAt: new Date()
              }
            };
            await bookingService.createBooking(bookingData);
            setShowBookingModal(false);
            confetti({
              particleCount: 120,
              spread: 90,
              origin: { y: 0.7 },
              emojis: ['🎉', '🥳', '🎟️', '🦄', '✨'],
              scalar: 1.2
            });
            setTimeout(() => {
              navigate('/bookings', { state: { justBooked: true } });
            }, 1200);
          } catch (error) {
            toast.error('Booking failed. Please try again.');
          }
        }}
        event={event}
        ticketTier={selectedTier}
        quantity={quantity}
        isProcessing={false}
      />
    </div>
  );
};

export default EventDetails; 